import { UserSignupPurpose } from "@common/types/user";
import { FlowContext, ctx } from "./flowContext";

export type PrimaryFlowSteps =
    | "purpose"
    | "profile"
    | "firmInfo"
    | "firmSetup"
    | "accountantPartnerCall"
    | "accountantPartnerCallOutro"
    | "waitlistOutro"
    | "outro"
    | "accountantOutro"
    | "enterpriseCall"
    | "connectAccounts"
    | "paymentProcessor"
    | "choosePlan"
    | "mobileBlock";

type StepPredicate = (ctx: FlowContext) => boolean;
type StepResolver = (ctx: FlowContext) => PrimaryFlowSteps | null;

export interface StepDef {
    /** Step is ready if this predicate is true (not done + accessible) */
    isReady: StepPredicate;

    /** Get next step(s) - returns first matching route */
    next: StepResolver;

    /** Optional: Get previous step */
    prev?: StepResolver;
}

export const steps: Record<PrimaryFlowSteps, StepDef> = {
    purpose: {
        isReady: (c) => !ctx.hasPurpose(c),
        next: (c) =>
            c.user.signupPurpose === UserSignupPurpose.ACCOUNTANT
                ? "firmInfo"
                : "profile",
        // No back - this is the first step
    },

    profile: {
        isReady: (c) => !ctx.hasProfile(c) && !ctx.isAccountant(c),
        next: (c) => {
            if (c.isMobile) {
                return "mobileBlock";
            }
            if (!ctx.isFromUS(c)) {
                return "waitlistOutro";
            }

            if (ctx.isAccountant(c)) {
                return "firmInfo";
            }
            return ctx.isFreshbooks(c) ? "choosePlan" : "connectAccounts";
        },
        prev: () => "purpose",
    },

    mobileBlock: {
        isReady: (c) => c.isMobile && ctx.hasProfile(c) && !ctx.isAccountant(c),
        next: () => null,
        // No back - terminal step
    },

    connectAccounts: {
        isReady: (c) => ctx.hasProfile(c) && !ctx.hasAccounts(c) && !c.isMobile,
        next: (c) => (ctx.isFreshbooks(c) ? "outro" : "paymentProcessor"),
        prev: (c) => {
            if (ctx.isFreshbooks(c)) {
                return "choosePlan";
            }
            return "profile";
        },
    },

    paymentProcessor: {
        isReady: (c) =>
            ctx.hasProfile(c) &&
            ctx.hasAccounts(c) &&
            !c.entity.profile?.paymentProcessorRequest &&
            !ctx.isFreshbooks(c) &&
            !c.isSubscribed,
        next: () => "choosePlan",
        prev: () => "connectAccounts",
    },

    choosePlan: {
        isReady: (c) => {
            if (!ctx.hasProfile(c)) {
                return false;
            }
            if (c.isSubscribed) {
                return false;
            }

            if (ctx.isFreshbooks(c)) {
                return true;
            }

            return (
                ctx.hasAccounts(c) &&
                !!c.entity.profile?.paymentProcessorRequest
            );
        },
        next: (c) => {
            if (ctx.isFreshbooks(c) && !ctx.hasAccounts(c)) {
                return "connectAccounts";
            }
            return "outro";
        },
        prev: (c) => {
            if (ctx.isFreshbooks(c)) {
                return "profile";
            }
            return "paymentProcessor";
        },
    },

    firmInfo: {
        isReady: (c) => ctx.isAccountant(c) && !ctx.hasFirmSize(c),
        next: () => "firmSetup",
        prev: () => "purpose",
    },

    firmSetup: {
        isReady: () => false, // Only reached via firmInfo or Back from later firm steps; the organization is created here
        next: (c) => {
            if (ctx.firmClientsOnlyOutsideOfUS(c)) {
                return "waitlistOutro";
            }

            if (ctx.needsAccountantCall(c)) {
                if (ctx.hasBookedEnterpriseCall(c)) {
                    return "accountantPartnerCallOutro";
                }
                return "accountantPartnerCall";
            }
            return "accountantOutro";
        },
        prev: () => "firmInfo",
    },

    accountantPartnerCall: {
        isReady: (c) =>
            ctx.isAccountant(c) &&
            ctx.needsAccountantCall(c) &&
            !ctx.hasBookedEnterpriseCall(c) &&
            !ctx.firmClientsOnlyOutsideOfUS(c), // Only navigated to from firmSetup
        next: () => "accountantPartnerCallOutro",
        prev: () => "firmSetup",
    },

    accountantPartnerCallOutro: {
        isReady: (c) => ctx.isAccountant(c) && ctx.hasBookedEnterpriseCall(c),
        next: () => "accountantOutro",
        prev: () => "firmSetup",
    },

    accountantOutro: {
        isReady: (c) => ctx.isAccountant(c) && !ctx.needsAccountantCall(c), // Only navigated to from firmSetup
        next: () => null, // Terminal
        // No back - terminal step
    },

    waitlistOutro: {
        isReady: (c) =>
            (!ctx.isAccountant(c) && !ctx.isFromUS(c)) ||
            (ctx.isAccountant(c) && ctx.firmClientsOnlyOutsideOfUS(c)),
        next: () => null, // Terminal
        prev: (c) => {
            if (ctx.isAccountant(c)) {
                return "firmSetup";
            }
            return "profile";
        },
    },

    outro: {
        isReady: (c) =>
            c.isSubscribed &&
            ctx.hasAccounts(c) &&
            ctx.isFromUS(c) &&
            !ctx.isAccountant(c),
        next: () => null, // Terminal
        // No back - terminal step
    },

    enterpriseCall: {
        isReady: () => false, // Only navigated to, not auto-detected
        next: (c) => {
            if (ctx.isFreshbooks(c) && !ctx.hasAccounts(c)) {
                return "connectAccounts";
            }
            return "outro";
        },
        prev: () => "choosePlan",
    },
};
